<template>
  <div class="insta-feed" :class="{ 'insta-feed--active': lightboxActive, 'insta-feed--quick': this.dragging }">
    <div
      class="insta-feed__gallery-container"
      @touchstart="setDragging($event, true)"
      @touchmove="setDragging($event, true)"
      @touchend="setDragging($event, false)"
    >
      <transition name="fade">
        <arrow-button
          v-if="controls && translate !== 0"
          primary
          left
          light
          label="Previous"
          class="insta-feed__gallery-control insta-feed__gallery-control--backward button--arrow-primary button--left"
          @click.native="slide('backward')"
        />
      </transition>
      <transition name="fade">
        <arrow-button
          v-if="controls && translate !== translateDistance"
          primary
          right
          light
          label="Next"
          class="insta-feed__gallery-control insta-feed__gallery-control--forward button--arrow-primary button--right"
          @click.native="slide('forward')"
        />
      </transition>
      <div class="insta-feed__gallery">
        <div
          id="insta-feed"
          class="insta-feed__widget"
          ref="gallerySlider"
        />
      </div>
    </div>
    <div class="insta-feed__username h7">@shikobeautyco</div>
  </div>
</template>

<script>
import Icon from '@/scripts/components/basic/Icon.vue';
import ArrowButton from '@/scripts/components/buttons/ArrowButton.vue'

export default {
  name: 'InstafeedController',
  components: {
    Icon,
    ArrowButton,
  },
  computed: {
    buttonStyles() {
      return {
        borderColor: '#fff'
      };
    },
    controls() {
      return this.$mq !== 'mobile';
    }
  },
  data() {
    return {
      attempts: 200,
      counter: 0,
      awaitInstafeed: 0,
      translateDistance: 0,
      slideWidth: 0,
      translate: 0,
      lightboxActive: false,
      dragging: false,
      begin: 0,
      previous: 0,
    }
  },
  mounted() {
    const settings = {
      root: null,
      threshold: 0,
    }

    const Observer = new IntersectionObserver(this.handleIntersect, settings);
    Observer.observe(this.$el);
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.removeTranslate);
    window.removeEventListener('resize', this.setBounds);
  },
  methods: {
    handleIntersect(entries, observer) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.initOnInstafeedLoad();
          observer.unobserve(this.$el);
        };
      })
    },
    initOnInstafeedLoad() {
      this.awaitInstafeed = setInterval(this.queryInstafeed, 50);
    },
    queryInstafeed() {
      if (this.$el.querySelector('.instafeed-lightbox')) {
        this.init();
        clearInterval(this.awaitInstafeed);
      }

      if (this.counter >= this.attempts) {
        console.error('Could not query Instafeed elements.');
        clearInterval(this.awaitInstafeed);
      }

      this.counter++;
    },
    init() {
      this.setBounds();

      window.addEventListener('popstate', this.removeTranslate);
      window.addEventListener('resize', this.setBounds);
    },
    removeTranslate() {
      const { gallerySlider } = this.$refs;
      const remove = window.location.hash.includes('insta-feed');

      if (remove) {
        gallerySlider.style.transform = 'none';
        this.lightboxActive = true;
      } else {
        gallerySlider.style.transform = `translateX(-${this.translate}px)`;

        requestAnimationFrame(() => {
          this.lightboxActive = false;
        });
      }      
    },
    setBounds(event) {
      const { gallerySlider } = this.$refs;     
      this.translateDistance = gallerySlider.scrollWidth - gallerySlider.offsetWidth;
      const singleSlide = gallerySlider.querySelector('.instafeed-container');

      if (singleSlide) {
        this.slideWidth = singleSlide.offsetWidth;
      }

      this.enforceBounds();

      if (event && event.type === 'resize') {
        gallerySlider.style.transform = `translateX(-${this.translate}px)`;
      }      
    },
    enforceBounds() {
      if (this.translate > this.translateDistance) {
        this.translate = this.translateDistance;
      } else if (this.translate < 0) {
        this.translate = 0;
      }
    },
    slide(direction) {
      this.setBounds();

      if (direction === 'backward') {
        this.translate = this.translate - (this.slideWidth * 2);
      } else if (direction === 'forward') {
        this.translate = this.translate + (this.slideWidth * 2);
      }

      this.enforceBounds();

      this.$refs.gallerySlider.style.transform = `translateX(-${this.translate}px)`;
    },
    setDragging(event, isDragging = false) {
      this.dragging = isDragging;
      document.body.style.overflow = this.dragging ? 'hidden' : 'unset';

      this.setBounds();
      if (!this.dragging) return;

      if (event.type === 'touchstart') {
        this.begin = event.offsetX || event.touches[0].clientX;
      }

      if (event.type === 'touchmove') {
        this.previous = event.offsetX || event.touches[0].clientX;
        const difference = (this.begin - this.previous) / 10;
        let moveTo = difference + this.translate;

        if (moveTo < 0) {
          moveTo = 0;
        } else if (moveTo > this.translateDistance) {
          moveTo = this.translateDistance;
        }

        this.$refs.gallerySlider.style.transform = `translateX(-${moveTo}px)`;
        this.translate = moveTo;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
  // Escalated specificity in the stylesheet is to override Instafeed's widget CSS.

  .insta-feed {
    @include mobile-only {
      padding-left: $grid-gutter-sm;
    }

    @include tablet-only {
      max-width: 100%;
    }

    @include tablet-up {
      padding-left: rem(196);
    }

    @include desktop-up {
      width: percentage(1137 / 1227);
      max-width: rem(1167);
      margin-left: rem(196);
      padding-left: 0;
    }
  }

  .insta-feed__gallery-container {
    position: relative;
  }

  // insta-feed overlays z-index at 100.
  .insta-feed__gallery-control {
    position: absolute;
    top: 50%;
    z-index: 101;
  }

  /deep/ .button--arrow-primary,
  /deep/ .button--arrow-primary:hover:not([disabled]) {
    border-color: $color-white;

    span::after {
      background-color: $color-white;
      transition: background-color 0.75s $default-animation-timing;
    }

    .icon {
      fill: $color-white;
      transition: fill 0.75s $default-animation-timing;
    }
  }

  /deep/ .button--arrow-primary:disabled {
    background: transparent;

    svg {
      opacity: 1;
    }

    span::after {
      opacity: 1;
      background-color: $color-black-400;
    }

    .icon {
      fill: $color-black-400 !important;
    }
  }

  .insta-feed__gallery-control--backward {
    left: 0;
    transform: translate(rem(-30), -50%);
  }

  .insta-feed__gallery-control--forward {
    transform: translateY(-50%);

    @include tablet-up {
      left: Min(rem(993), 75%);
    }

    @include desktop-up {
      left: Min(rem(993), 80%);
    }

    @media (min-width: 1360px) {
      left: Min(rem(993), 88.49%);
    }
  }

  .insta-feed__gallery {
    overflow: hidden;
    white-space: nowrap;
  }  

  .insta-feed__widget {
    margin-top: 0 !important;
    overflow: visible;
    transition: transform 0.7s $ease-in-out-circ;

    .insta-feed--active & {
      transform: none !important;
      transition: none;
    }

    .insta-feed--quick & {
      transition: transform 0.1s linear;
    }
  }

  /deep/ .insta-feed__widget > a:not(:last-of-type) .instafeed-container {
      margin-right: rem(8);
    }

  /deep/ .instafeed-container {
    width: rem(155) !important;
    height: rem(155) !important;
    @include tablet-up {
      width: rem(260) !important;
      height: rem(260) !important;
    }
  }

  .insta-feed__username {
    color: $color-white;
    font-family: $font-family-primary;
    letter-spacing: 0;
    margin-top: 10px;
  }
</style>